import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchScheduledArticles, deleteArticle } from "../services/api";
import { CalendarClock, ArrowLeft, Pencil, Trash2, Loader2, FileText } from "lucide-react";

const formatAest = (utcStr) => {
  if (!utcStr) return "";
  const d = new Date(utcStr);
  return d.toLocaleString("en-AU", {
    timeZone: "Australia/Sydney",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }) + " AEST";
};

const ScheduledArticles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadScheduled = async () => {
    setLoading(true);
    try {
      const data = await fetchScheduledArticles();
      setArticles(data.articles || []);
    } catch (err) {
      console.error("Failed to load scheduled articles:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadScheduled();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await deleteArticle(id);
      setArticles((prev) => prev.filter((a) => a.id !== id));
    } catch {
      alert("Failed to delete");
    }
  };

  const drafts = articles.filter((a) => a.status === "draft");
  const scheduled = articles.filter((a) => a.status === "scheduled");

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Link
            to="/"
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <ArrowLeft size={20} />
          </Link>
          <div className="flex items-center gap-2">
            <CalendarClock size={22} className="text-indigo-500" />
            <h1 data-testid="scheduled-page-title" className="text-2xl font-black text-gray-900">
              Drafts & Scheduled
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#1B7A3D]" size={28} />
          </div>
        ) : articles.length === 0 ? (
          <div data-testid="no-scheduled-articles" className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <FileText className="mx-auto text-gray-300 mb-4" size={48} />
            <h2 className="text-lg font-bold text-gray-900 mb-2">No Drafts or Scheduled Articles</h2>
            <p className="text-sm text-gray-500 mb-6">
              Save a draft or schedule an article from the Write page.
            </p>
            <Link
              to="/write"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1B7A3D] hover:bg-[#15612F] text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Write Article
            </Link>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Drafts Section */}
            {drafts.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-5 bg-amber-500 rounded-full" />
                  <h2 className="text-base font-bold text-gray-900">Drafts ({drafts.length})</h2>
                </div>
                <div className="space-y-3">
                  {drafts.map((article) => (
                    <ArticleRow key={article.id} article={article} onDelete={handleDelete} badge="draft" />
                  ))}
                </div>
              </div>
            )}

            {/* Scheduled Section */}
            {scheduled.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-5 bg-indigo-500 rounded-full" />
                  <h2 className="text-base font-bold text-gray-900">Scheduled ({scheduled.length})</h2>
                </div>
                <div className="space-y-3">
                  {scheduled.map((article) => (
                    <ArticleRow key={article.id} article={article} onDelete={handleDelete} badge="scheduled" />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ArticleRow = ({ article, onDelete, badge }) => (
  <div
    data-testid={`scheduled-article-${article.id}`}
    className="bg-white rounded-2xl border border-gray-100 p-5 flex items-start gap-4 hover:shadow-md transition-shadow"
  >
    {article.image && (
      <img
        src={article.image.startsWith("http") ? article.image : `${process.env.REACT_APP_BACKEND_URL}${article.image}`}
        alt=""
        className="w-24 h-16 rounded-lg object-cover flex-shrink-0"
      />
    )}
    <div className="flex-1 min-w-0">
      <h3 className="text-base font-bold text-gray-900 truncate">{article.title}</h3>
      <p className="text-sm text-gray-500 truncate mt-0.5">{article.excerpt || "No excerpt"}</p>
      <div className="flex items-center gap-3 mt-2">
        {badge === "draft" ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">
            <FileText size={12} />
            Draft
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">
            <CalendarClock size={12} />
            {formatAest(article.scheduled_at)}
          </span>
        )}
        <span className="text-xs text-gray-400">{article.category}</span>
      </div>
    </div>
    <div className="flex items-center gap-1 flex-shrink-0">
      <Link
        to={`/edit/${article.id}`}
        className="p-2 rounded-lg hover:bg-green-50 text-gray-400 hover:text-[#1B7A3D] transition-all"
        title="Edit"
      >
        <Pencil size={16} />
      </Link>
      <button
        onClick={() => onDelete(article.id)}
        className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all"
        title="Delete"
      >
        <Trash2 size={16} />
      </button>
    </div>
  </div>
);

export default ScheduledArticles;
